import CloseIcon from '@mui/icons-material/CloseRounded';
import FullscreenIcon from '@mui/icons-material/FullscreenRounded';
import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { useCaptureException, ZBError } from '@zenbusiness/application-commons-logging';
import isValidUuid from '@zenbusiness/application-commons-utils/isValidUuid';

import { AssistantUi } from '#client/directEntry/MFEs';
import { getAppSection } from '#client/utils/getAppSection';

import VeloLogotype from './assets/velo-logotype.svg';
import { VELO_SIDE_PANEL_CLICKSTREAM_POSITION, VELO_SIDE_PANEL_SOURCE_COMPONENT } from './constants';
import { useVeloPanel } from './hooks/useVeloPanel';
import { buildVeloPageUrl } from './utils/buildVeloPageUrl';
import * as Styled from './VeloSidePanel.style';

function VeloSidePanel() {
  const { page, closePanel, threadUuid, setThreadUuid } = useVeloPanel();
  const { businessEntityUuid } = useBusinessEntity();
  const captureException = useCaptureException();
  const { formatMessage } = useIntl();

  function handleStateChange({ threadUuid: nextThreadUuid }) {
    setThreadUuid(isValidUuid(nextThreadUuid) ? nextThreadUuid : null);
  }

  function storeNavigationContext() {
    const navigationContext = {
      referrer: window.location.pathname,
      businessEntityUuid,
      section: getAppSection(window.location.pathname) || 'unknown',
      timestamp: Date.now(),
      clickstreamContext: {
        elementName: 'velo_side_panel_fullscreen',
        elementPosition: VELO_SIDE_PANEL_CLICKSTREAM_POSITION,
      },
    };

    try {
      sessionStorage.setItem('velo_source_context', JSON.stringify(navigationContext));
    } catch (error) {
      captureException(
        new ZBError(error, {
          message: 'Failed to store Velo navigation context',
          contextData: { details: { operation: 'storeNavigationContext' } },
        }),
      );
    }
  }

  function handleFullScreenClick() {
    storeNavigationContext();
    window.location.href = threadUuid ? `/guide/thread/${threadUuid}` : buildVeloPageUrl(businessEntityUuid, page);
  }

  const assistantProps = {
    exportName: 'VeloQA',
    tag: page.tag,
    origination: page.origination,
    initialState: {
      sourceComponent: VELO_SIDE_PANEL_SOURCE_COMPONENT,
      ...(threadUuid ? { threadUuid } : {}),
    },
    onStateChange: handleStateChange,
    showInputAvatar: true,
    autoFocusInput: false,
  };

  return (
    <Styled.Root component="aside">
      <Styled.TopBar>
        <Styled.Logo>
          <VeloLogotype role="img" title={formatMessage({ id: 'dashboard.veloSidePanel.logoAlt' })} />
        </Styled.Logo>
        <Styled.Actions>
          <Styled.ActionButton
            label={formatMessage({ id: 'dashboard.veloSidePanel.fullScreen' })}
            aria-label={formatMessage({ id: 'dashboard.veloSidePanel.fullScreen' })}
            onClick={handleFullScreenClick}
            clickstreamProps={{
              elementName: 'velo_side_panel_fullscreen',
              elementPosition: VELO_SIDE_PANEL_CLICKSTREAM_POSITION,
            }}
          >
            <FullscreenIcon />
          </Styled.ActionButton>
          <Styled.ActionButton
            label={formatMessage({ id: 'dashboard.veloSidePanel.close' })}
            aria-label={formatMessage({ id: 'dashboard.veloSidePanel.close' })}
            onClick={closePanel}
            clickstreamProps={{
              elementName: 'velo_side_panel_close',
              elementPosition: VELO_SIDE_PANEL_CLICKSTREAM_POSITION,
            }}
          >
            <CloseIcon />
          </Styled.ActionButton>
        </Styled.Actions>
      </Styled.TopBar>

      <Styled.Body>
        <AssistantUi key={page.path} {...assistantProps} />
      </Styled.Body>
    </Styled.Root>
  );
}

export default VeloSidePanel;
